import { createContext, type PropsWithChildren, type ReactNode } from "react";
import { Fragment } from "react";
import * as DM from "@radix-ui/react-dropdown-menu";
import * as styles from "./DropdownMenu.styles.tsx";
import { useDropdownMenuContext } from "src/components/DropdownMenu/useDropdownMenuContext.ts";

const Noop = ({ children }: PropsWithChildren) => children;

export type TriggerBtnVariant = "standard" | "filled";

export type DropdownMenuV2Props = {
  open?: boolean;
  defaultOpen?: boolean;
  contentPlacement?: "top" | "right" | "bottom" | "left";
  contentAlign?: "start" | "center" | "end";
  sideOffset?: number;
  renderInPortal?: boolean;
  className?: string;
  disabled?: boolean;
  customWidth?: number;
  value?: string;
  items: {
    label: ReactNode;
    onSelect?(event: Event): void;
    disabled?: boolean;
    value: string;
  }[];
  onOpenChange?(open: boolean): void;
};

export const DropdownMenuContext = createContext<{
  disabled: boolean;
} | null>(null);

const DropdownMenuV2Impl = (props: PropsWithChildren<DropdownMenuV2Props>) => {
  const {
    open,
    defaultOpen,
    contentPlacement = "bottom",
    contentAlign = "center",
    sideOffset = 0,
    items,
    renderInPortal = true,
    className,
    disabled = false,
    children,
    onOpenChange,
    customWidth,
    value,
  } = props;

  const PortalComponent = renderInPortal ? DM.Portal : Noop;

  return (
    <DropdownMenuContext.Provider value={{ disabled }}>
      <div css={styles.containerStyle} className={className}>
        <DM.Root
          open={open}
          defaultOpen={defaultOpen}
          onOpenChange={onOpenChange}
        >
          {children}
          <PortalComponent>
            <DM.Content
              css={styles.contentStyles(customWidth)}
              side={contentPlacement}
              sideOffset={sideOffset}
              align={contentAlign}
            >
              {items.map(
                ({ label, disabled, onSelect, value: itemValue }, i) => (
                  <Fragment key={i}>
                    <DM.Item
                      css={styles.itemStyles({
                        selected: value === itemValue,
                      })}
                      disabled={disabled}
                      onSelect={onSelect}
                    >
                      {label}
                    </DM.Item>

                    {items.length && i !== items.length - 1 && (
                      <DM.Separator css={styles.separatorStyles} />
                    )}
                  </Fragment>
                ),
              )}
            </DM.Content>
          </PortalComponent>
        </DM.Root>
      </div>
    </DropdownMenuContext.Provider>
  );
};

const DropdownTrigger = ({
  triggerBtnVariant = "standard",
  children,
}: PropsWithChildren<{
  triggerBtnVariant?: TriggerBtnVariant;
}>) => {
  const { disabled } = useDropdownMenuContext();
  return (
    <DM.Trigger css={styles.btnStyle({ triggerBtnVariant, disabled })}>
      {children}
    </DM.Trigger>
  );
};

const DropdownArrow = () => {
  return <i className={"icon-15-arw_dw"} id="dm-btn-arrow" />;
};

const DropdownLabel = ({ children }: PropsWithChildren) => {
  return <span>{children}</span>;
};

export const DropdownMenu = Object.assign(DropdownMenuV2Impl, {
  Trigger: DropdownTrigger,
  Arrow: DropdownArrow,
  Label: DropdownLabel,
});
